import { Loader2, Inbox, Sparkles } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const STATUS_STYLES = {
  修了: "bg-green-100 text-green-800",
  合格: "bg-green-100 text-green-800",
  実施済: "bg-green-100 text-green-800",
  確認済: "bg-green-100 text-green-800",
  契約中: "bg-green-100 text-green-800",
  在籍: "bg-green-100 text-green-800",
  支払済: "bg-green-100 text-green-800",
  視聴中: "bg-blue-100 text-blue-800",
  動画完了: "bg-blue-100 text-blue-800",
  受講中: "bg-blue-100 text-blue-800",
  準備中: "bg-slate-100 text-slate-700",
  予定: "bg-slate-100 text-slate-700",
  未開始: "bg-slate-100 text-slate-700",
  未実施: "bg-slate-100 text-slate-700",
  未作成: "bg-slate-100 text-slate-700",
  テスト未実施: "bg-amber-100 text-amber-800",
  所感未提出: "bg-amber-100 text-amber-800",
  補講対象: "bg-amber-100 text-amber-800",
  休職: "bg-amber-100 text-amber-800",
  期限超過: "bg-rose-100 text-rose-800",
  不合格: "bg-rose-100 text-rose-800",
  利用停止: "bg-rose-100 text-rose-800",
  退職: "bg-slate-200 text-slate-600",
};

export function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || "bg-slate-100 text-slate-700";
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>
      {status}
    </span>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, unit, icon: Icon, tone = "blue", testid }) {
  const tones = {
    blue: "text-blue-600 bg-blue-50",
    green: "text-green-600 bg-green-50",
    amber: "text-amber-600 bg-amber-50",
    rose: "text-rose-600 bg-rose-50",
    slate: "text-slate-600 bg-slate-100",
  };
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <span className="text-sm text-slate-500">{label}</span>
        {Icon && <span className={`p-2 rounded-lg ${tones[tone]}`}><Icon className="w-4 h-4" /></span>}
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-3xl font-bold text-slate-900">{value}</span>
        {unit && <span className="text-sm text-slate-500">{unit}</span>}
      </div>
    </div>
  );
}

export function Loading() {
  return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
    </div>
  );
}

export function EmptyState({ title = "データがありません", hint }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="p-4 rounded-full bg-slate-100 mb-3"><Inbox className="w-7 h-7 text-slate-400" /></div>
      <p className="text-slate-600 font-medium">{title}</p>
      {hint && <p className="text-sm text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export function AiDraftNotice({ className = "" }) {
  return (
    <div data-testid="ai-draft-notice" className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium ${className}`}>
      <Sparkles className="w-3.5 h-3.5" /> AI生成案・管理者確認前
    </div>
  );
}

export function OfficeSelect({ offices, value, onChange, testid = "office-filter" }) {
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger className="w-48 bg-white" data-testid={testid}><SelectValue placeholder="事業所" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">全事業所</SelectItem>
        {offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function groupByOffice(items, offices, getOfficeIds) {
  const sections = offices.map((o) => ({ office: o, items: [] }));
  const byId = Object.fromEntries(sections.map((s) => [s.office.id, s]));
  const unassigned = { office: { id: "__none__", name: "未所属・未設定" }, items: [] };
  items.forEach((it) => {
    const ids = (getOfficeIds(it) || []).filter(Boolean);
    if (!ids.length) { unassigned.items.push(it); return; }
    ids.forEach((id) => { (byId[id] || unassigned).items.push(it); });
  });
  const res = sections.filter((s) => s.items.length);
  if (unassigned.items.length) res.push(unassigned);
  return res;
}

export function OfficeSectionHeader({ name, count }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200" data-testid={`office-section-${name}`}>
      <span className="text-sm font-semibold text-slate-700">{name}</span>
      <span className="text-xs text-slate-400">{count}件</span>
    </div>
  );
}
